import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef, input, output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MenuItem, MessageService } from 'primeng/api';
import { Toast } from 'primeng/toast';
import { MenuModule } from 'primeng/menu';
import { firstValueFrom } from 'rxjs';
import { OciService } from '../../../services/oci.service';
import { ProjectphotoService } from '../../../services/projectphoto.service';
import { BlockScheduleFileService } from '../../../services/blockschedulefile.service';
import { ProjectPhotoUnifiedModel } from '../../../models/projectphoto.model';
import { environment } from '../../../../environments/environment';
import WebViewer, { WebViewerInstance } from '@pdftron/webviewer';
import { ProjectStateService } from '../../../services/project-state.service';
import { ProjectModel } from '../../../models/project.model';
import { buildProjectAssetDownloadFileName } from '../../../utils/download-file-name.utils';
import { MediaFallbackService } from '../../../services/media-fallback.service';

@Component({
  selector: 'app-photo-viewer',
  standalone: true,
  imports: [CommonModule, Toast, MenuModule],
  templateUrl: './photo-viewer.component.html',
  styleUrl: './photo-viewer.component.css'
})
export class PhotoViewerComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('viewerContainer', { static: false }) viewerContainer!: ElementRef;

  /** Supplies a photo when the viewer is hosted inside another component's dialog. */
  photo = input<ProjectPhotoUnifiedModel | null>(null);
  /** Limits embedded navigation to the photos supplied by the hosting control. */
  embeddedPhotos = input<ProjectPhotoUnifiedModel[] | null>(null);
  photoChanged = output<ProjectPhotoUnifiedModel>();
  photoProjectId = input<number>(0);
  readOnly = input<boolean>(false);
  embedded = input<boolean>(false);

  projectId: number = 0;
  currentPhoto: ProjectPhotoUnifiedModel | null = null;
  allPhotos: ProjectPhotoUnifiedModel[] = [];
  currentPhotoIndex: number = 0;
  currentProject: ProjectModel | null = null;
  isReadOnly: boolean = false;
  private checklistReturnUrl: string | null = null;

  isLoading: boolean = true;
  private webViewerInstance: WebViewerInstance | null = null;
  private annotationSaveTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private annotationDirty = false;
  private isLoadingServerAnnotations = false;
  private readonly annotationSaveDelayMs = 1000;
  private activeDocumentObjectUrl: string | null = null;
  private currentAnnotationXfdf = '';
  isDownloading = false;

  private readonly missingMetadataLabel = 'Not available';
  readonly downloadMenuItems: MenuItem[] = [
    { label: 'Original File', icon: 'pi pi-file', command: () => void this.downloadOriginalPhoto() },
    { label: 'Optimized File', icon: 'pi pi-image', command: () => void this.downloadOptimizedPhoto() }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ociService: OciService,
    private projectPhotoService: ProjectphotoService,
    private blockScheduleFileService: BlockScheduleFileService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
    private projectStateService: ProjectStateService,
    private mediaFallbackService: MediaFallbackService
  ) {}

  ngOnInit(): void {
    const embeddedPhoto = this.photo();
    if (this.embedded() && embeddedPhoto) {
      this.projectId = this.photoProjectId();
      const photos = this.embeddedPhotos()?.filter(photo => !!photo?.photoPath) ?? [];
      this.allPhotos = photos.length > 0 ? photos : [embeddedPhoto];
      this.currentPhotoIndex = this.allPhotos.findIndex(photo => photo.photoId === embeddedPhoto.photoId);
      if (this.currentPhotoIndex < 0) {
        this.currentPhotoIndex = 0;
      }
      this.currentPhoto = this.allPhotos[this.currentPhotoIndex];
      this.isReadOnly = this.readOnly();
      return;
    }

    const projectIdParam = this.route.snapshot.paramMap.get('projectId');
    if (!projectIdParam) {
      this.goBackToPhotos();
      return;
    }

    this.projectId = parseInt(projectIdParam);
    this.currentProject = this.projectStateService.getCurrentProject();
    this.isReadOnly = this.route.snapshot.queryParamMap.get('readonly') === 'true';
    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    this.checklistReturnUrl = returnUrl?.startsWith('/skidchecksheet/') ? returnUrl : null;

    const navigation = this.router.getCurrentNavigation();
    const state = navigation?.extras.state || (window.history.state);

    if (!state || !state.photoList) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Photo data not available. Please go back and try again.'
      });
      this.goBackToPhotos();
      return;
    }

    this.allPhotos = state.photoList;

    if (state.selectedPhotoId) {
      this.currentPhotoIndex = this.allPhotos.findIndex(p => p.photoId === state.selectedPhotoId);
      if (this.currentPhotoIndex === -1) {
        this.currentPhotoIndex = 0;
      }
    }

    this.currentPhoto = this.allPhotos[this.currentPhotoIndex];
  }

  ngAfterViewInit(): void {
    this.initializeAndLoad();
  }

  private async initializeAndLoad(): Promise<void> {
    try {
      this.isLoading = true;
      await this.initializeWebViewer();
      await this.loadPhoto();
    } catch (error) {
      console.error('Error initializing photo viewer:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to initialize viewer'
      });
      this.isLoading = false;
    }
  }

  private async initializeWebViewer(): Promise<void> {
    if (!this.viewerContainer?.nativeElement) return;

    this.webViewerInstance = await WebViewer.Iframe(
      {
        path: 'assets/webviewer/lib',
        licenseKey: environment.apryseWebLicenseKey,
        enableAnnotations: !this.isReadOnly,
      },
      this.viewerContainer.nativeElement
    );

    this.webViewerInstance.UI.enableFeatures([
      this.webViewerInstance.UI.Feature.Search,
      this.webViewerInstance.UI.Feature.TextSelection,
    ]);

    this.applyAnnotationFeatureAvailability();

    if (!this.isReadOnly) {
      this.attachAnnotationListeners();
    }
    await new Promise<void>((resolve) => setTimeout(() => resolve(), 1000));
  }

  private async loadPhoto(): Promise<void> {
    if (!this.webViewerInstance || !this.currentPhoto?.photoPath) return;

    this.releaseActiveDocumentObjectUrl();
    let documentUrl = this.mediaFallbackService.buildCdnUrl(this.currentPhoto.photoPath);
    const fileName = this.currentPhoto.photoName || 'photo.jpg';
    let extension = fileName.split('.').pop()?.toLowerCase() || 'jpg';
    if (extension === 'jfif') {
      extension = 'jpg';
    }

    const loadOptions: any = { filename: fileName };
    if (['png', 'jpg', 'jpeg', 'jfif', 'gif', 'bmp', 'tif', 'tiff', 'webp'].includes(extension)) {
      loadOptions.extension = extension;
      try {
        const image = await this.loadImageElement(documentUrl);
        loadOptions.pageSizes = [this.getLoadedImagePageSize(image)];
      } catch (error) {
        console.warn('CDN photo image failed before Apryse load, retrying through OCI fallback:', error);
        const fallbackUrl = await this.mediaFallbackService.getFallbackObjectUrl(this.currentPhoto.photoPath);
        if (!fallbackUrl) {
          throw error;
        }
        this.activeDocumentObjectUrl = fallbackUrl;
        documentUrl = fallbackUrl;

        const image = await this.loadImageElement(documentUrl);
        loadOptions.pageSizes = [this.getLoadedImagePageSize(image)];
      }
    }

    await this.loadServerAnnotationXfdf();
    this.applyAnnotationFeatureAvailability();
    try {
      await this.webViewerInstance.Core.documentViewer.loadDocument(documentUrl, loadOptions);
    } catch (error) {
      console.warn('CDN photo load failed, retrying through OCI fallback:', error);
      const fallbackUrl = await this.mediaFallbackService.getFallbackObjectUrl(this.currentPhoto.photoPath);
      if (!fallbackUrl) {
        throw error;
      }
      this.activeDocumentObjectUrl = fallbackUrl;
      await this.webViewerInstance.Core.documentViewer.loadDocument(fallbackUrl, loadOptions);
    }
    await this.importServerAnnotations();

    this.annotationDirty = false;
    this.isLoading = false;
    this.cdr.detectChanges();
  }

  goBackToPhotos(): void {
    if (this.checklistReturnUrl) {
      this.router.navigateByUrl(this.checklistReturnUrl);
      return;
    }

    this.router.navigate(['/photos', this.projectId]);
  }

  async goToPreviousPhoto(): Promise<void> {
    if (this.currentPhotoIndex > 0) {
      await this.persistAnnotationsNow();
      this.currentPhotoIndex--;
      this.currentPhoto = this.allPhotos[this.currentPhotoIndex];
      this.photoChanged.emit(this.currentPhoto);
      await this.loadPhoto();
    }
  }

  async goToNextPhoto(): Promise<void> {
    if (this.currentPhotoIndex < this.allPhotos.length - 1) {
      await this.persistAnnotationsNow();
      this.currentPhotoIndex++;
      this.currentPhoto = this.allPhotos[this.currentPhotoIndex];
      this.photoChanged.emit(this.currentPhoto);
      await this.loadPhoto();
    }
  }

  get hasPreviousPhoto(): boolean {
    return this.currentPhotoIndex > 0;
  }

  get hasNextPhoto(): boolean {
    return this.currentPhotoIndex < this.allPhotos.length - 1;
  }

  get currentPageInfo(): string {
    if (!this.allPhotos.length) return '';
    return `${this.currentPhotoIndex + 1} of ${this.allPhotos.length}`;
  }

  get parsedMetadata(): { capturedAt?: string; latitude?: number; longitude?: number } | null {
    const rawMetadata = this.currentPhoto?.metaData;
    if (!rawMetadata) {
      return null;
    }

    try {
      return JSON.parse(rawMetadata) as { capturedAt?: string; latitude?: number; longitude?: number };
    } catch (error) {
      console.warn('Unable to parse photo metadata:', error);
      return null;
    }
  }

  get uploadedByDisplay(): string {
    const name = this.currentPhoto?.createdByName?.trim();
    if (name) {
      return name;
    }

    if (this.currentPhoto?.createdBy) {
      return `User #${this.currentPhoto.createdBy}`;
    }

    return this.missingMetadataLabel;
  }

  get capturedAtDisplay(): string {
    return this.parsedMetadata?.capturedAt || this.missingMetadataLabel;
  }

  get latitudeDisplay(): string {
    const latitude = this.parsedMetadata?.latitude;
    return typeof latitude === 'number' ? latitude.toFixed(6) : this.missingMetadataLabel;
  }

  get longitudeDisplay(): string {
    const longitude = this.parsedMetadata?.longitude;
    return typeof longitude === 'number' ? longitude.toFixed(6) : this.missingMetadataLabel;
  }

  get sourceDisplay(): string {
    if (!this.currentPhoto) {
      return this.missingMetadataLabel;
    }

    if (this.currentPhoto.sourceType === 'Skid') {
      return (this.currentPhoto.skidNumber || '').replace(/^skid\s*/i, 'MVPS ') || 'MVPS';
    }

    return 'Project';
  }

  openDownloadMenu(event: MouseEvent, menu: any): void {
    event.stopPropagation();
    menu.toggle(event);
  }

  async downloadOriginalPhoto(): Promise<void> {
    if (this.isDownloading) return;

    this.setDownloadingState(true);
    try {
      if (this.supportsAnnotatedPhotos) {
        await this.persistAnnotationsNow();
        await this.downloadAnnotatedPhotoThroughApi(false);
        return;
      }

      if (!this.currentPhoto?.photoPath) return;
      await this.downloadPhotoWithMetadata(this.currentPhoto.photoPath, this.currentPhoto.photoName || 'photo');
    } finally {
      this.setDownloadingState(false);
    }
  }

  async downloadOptimizedPhoto(): Promise<void> {
    if (this.isDownloading) return;

    this.setDownloadingState(true);
    try {
      if (this.supportsAnnotatedPhotos) {
        await this.persistAnnotationsNow();
        await this.downloadAnnotatedPhotoThroughApi(true);
        return;
      }

      if (!this.currentPhoto?.thumbnailPath) return;
      await this.downloadPhotoWithMetadata(this.currentPhoto.thumbnailPath, this.currentPhoto.photoName || 'photo');
    } finally {
      this.setDownloadingState(false);
    }
  }

  private setDownloadingState(isDownloading: boolean): void {
    this.isDownloading = isDownloading;
    this.cdr.detectChanges();
  }

  private get supportsAnnotatedPhotos(): boolean {
    return !this.isReadOnly && (this.currentPhoto?.sourceType === 'Project' || this.currentPhoto?.sourceType === 'Skid');
  }

  private attachAnnotationListeners(): void {
    if (!this.webViewerInstance) {
      return;
    }

    const { annotationManager } = this.webViewerInstance.Core;
    annotationManager.addEventListener('annotationChanged', (_annotations: any[], _action: string, info: any) => {
      if (!this.supportsAnnotatedPhotos || this.isLoadingServerAnnotations) {
        return;
      }

      if (info?.imported) {
        return;
      }

      this.annotationDirty = true;
      this.scheduleAnnotationSave();
    });
  }

  private async loadServerAnnotationXfdf(): Promise<void> {
    if (!this.webViewerInstance) {
      return;
    }

    this.currentAnnotationXfdf = '';
    if (!this.supportsAnnotatedPhotos) {
      return;
    }

    const annotation = this.currentPhoto?.sourceType === 'Project'
      ? await firstValueFrom(this.projectPhotoService.getProjectPhotoAnnotation(this.currentPhoto.photoId))
      : await firstValueFrom(this.blockScheduleFileService.getFileAnnotation(this.currentPhoto!.photoId));
    this.currentAnnotationXfdf = annotation.annotationXfdf || '';
  }

  private async importServerAnnotations(): Promise<void> {
    if (!this.webViewerInstance || !this.currentAnnotationXfdf) {
      return;
    }

    this.isLoadingServerAnnotations = true;
    try {
      await this.webViewerInstance.Core.annotationManager.importAnnotations(this.currentAnnotationXfdf);
    } finally {
      this.isLoadingServerAnnotations = false;
    }
  }

  private applyAnnotationFeatureAvailability(): void {
    if (!this.webViewerInstance) {
      return;
    }

    const annotationFeatures = [
      this.webViewerInstance.UI.Feature.Annotations,
      this.webViewerInstance.UI.Feature.NotesPanel,
    ];

    if (this.supportsAnnotatedPhotos) {
      this.webViewerInstance.UI.enableFeatures(annotationFeatures);
    } else {
      this.webViewerInstance.UI.disableFeatures(annotationFeatures);
    }
  }

  private scheduleAnnotationSave(): void {
    if (this.annotationSaveTimeoutId) {
      clearTimeout(this.annotationSaveTimeoutId);
    }

    this.annotationSaveTimeoutId = setTimeout(() => {
      void this.persistAnnotationsNow();
    }, this.annotationSaveDelayMs);
  }

  private async persistAnnotationsNow(): Promise<void> {
    if (!this.webViewerInstance || !this.supportsAnnotatedPhotos || !this.annotationDirty) {
      return;
    }

    if (this.annotationSaveTimeoutId) {
      clearTimeout(this.annotationSaveTimeoutId);
      this.annotationSaveTimeoutId = null;
    }

    const { annotationManager } = this.webViewerInstance.Core;
    const annotations = annotationManager.getAnnotationsList();
    const annotationXfdf = annotations.length > 0
      ? await annotationManager.exportAnnotations({
          generateInlineAppearances: true,
          links: false,
          widgets: false
        })
      : null;

    if (this.currentPhoto?.sourceType === 'Project') {
      await firstValueFrom(this.projectPhotoService.updateProjectPhotoAnnotation(this.currentPhoto.photoId, annotationXfdf));
    } else {
      await firstValueFrom(this.blockScheduleFileService.updateFileAnnotation(this.currentPhoto!.photoId, annotationXfdf));
    }
    this.annotationDirty = false;
  }

  private async downloadAnnotatedPhotoThroughApi(optimized: boolean): Promise<void> {
    if (!this.currentPhoto) {
      return;
    }

    try {
      const blob = this.currentPhoto.sourceType === 'Project'
        ? await firstValueFrom(this.projectPhotoService.downloadProjectPhoto(this.currentPhoto.photoId, optimized))
        : await firstValueFrom(this.blockScheduleFileService.downloadFile(this.currentPhoto.photoId, optimized));
      const stampedBlob = await this.createStampedPhotoBlob(blob, this.currentPhoto.photoName || 'photo');
      const stampedFileName = this.getStampedFileName(this.currentPhoto.photoName || 'photo', stampedBlob.type);
      this.triggerBlobDownload(stampedBlob, stampedFileName);
    } catch (error) {
      console.error('Error downloading annotated photo:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to download photo.'
      });
    }
  }

  private async downloadPhotoWithMetadata(filePath: string, fileName: string): Promise<void> {
    try {
      const originalBlob = await firstValueFrom(this.ociService.getOciResourceEnhanced(filePath));
      const stampedBlob = await this.createStampedPhotoBlob(originalBlob, fileName);
      const stampedFileName = this.getStampedFileName(fileName, stampedBlob.type);
      this.triggerBlobDownload(stampedBlob, stampedFileName);
    } catch (error) {
      console.error('Error downloading photo with metadata:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to download photo.'
      });
    }
  }

  private async createStampedPhotoBlob(blob: Blob, fileName: string): Promise<Blob> {
    const targetMimeType = this.getCanvasOutputMimeType(fileName);
    const imageUrl = URL.createObjectURL(blob);

    try {
      const image = await this.loadImageElement(imageUrl);
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth || image.width;
      canvas.height = image.naturalHeight || image.height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Canvas context is unavailable');
      }

      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      this.drawMetadataStamp(ctx, canvas.width, canvas.height);

      const renderedBlob = await this.canvasToBlob(canvas, targetMimeType);
      return renderedBlob;
    } catch (error) {
      console.warn('Falling back to raw download because metadata stamping failed:', error);
      return blob;
    } finally {
      URL.revokeObjectURL(imageUrl);
    }
  }

  private drawMetadataStamp(ctx: CanvasRenderingContext2D, width: number, height: number): void {
    const lines = this.getMetadataStampLines();
    if (lines.length === 0) {
      return;
    }

    const fontSize = Math.max(12, Math.round(width * 0.014));
    const lineHeight = Math.round(fontSize * 1.45);
    const padding = Math.max(12, Math.round(width * 0.012));
    const maxTextWidth = Math.min(width * 0.5, width - (padding * 2));

    ctx.save();
    ctx.font = `500 ${fontSize}px Inter, Arial, sans-serif`;

    const wrappedLines = lines.flatMap((line) => this.wrapText(ctx, line, maxTextWidth));
    const boxWidth = Math.min(
      maxTextWidth + (padding * 2),
      Math.max(...wrappedLines.map((line) => ctx.measureText(line).width), 0) + (padding * 2)
    );
    const boxHeight = (wrappedLines.length * lineHeight) + (padding * 2);
    const boxX = padding;
    const boxY = height - boxHeight - padding;

    ctx.fillStyle = 'rgba(9, 31, 45, 0.68)';
    ctx.fillRect(boxX, boxY, boxWidth, boxHeight);

    ctx.fillStyle = '#ffffff';
    ctx.textBaseline = 'top';

    wrappedLines.forEach((line, index) => {
      ctx.fillText(line, boxX + padding, boxY + padding + (index * lineHeight));
    });

    ctx.restore();
  }

  private getMetadataStampLines(): string[] {
    return [
      `Uploaded By: ${this.uploadedByDisplay}`,
      `Captured At: ${this.capturedAtDisplay}`,
      `Latitude: ${this.latitudeDisplay}`,
      `Longitude: ${this.longitudeDisplay}`
    ];
  }

  private wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
      const trialLine = currentLine ? `${currentLine} ${word}` : word;
      if (ctx.measureText(trialLine).width <= maxWidth || !currentLine) {
        currentLine = trialLine;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    }

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines;
  }

  private getCanvasOutputMimeType(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (extension === 'png') {
      return 'image/png';
    }
    if (extension === 'webp') {
      return 'image/webp';
    }
    return 'image/jpeg';
  }

  private getStampedFileName(fileName: string, mimeType: string): string {
    const extension = mimeType === 'image/png'
      ? 'png'
      : mimeType === 'image/webp'
        ? 'webp'
        : 'jpg';

    return buildProjectAssetDownloadFileName(this.currentProject, 'Photo', fileName, {
      skid: this.currentPhoto?.sourceType === 'Skid'
        ? {
            name: this.currentPhoto.skidNumber || null,
            integratorSkidSerialNo: this.currentPhoto.photoName || null,
          }
        : null,
      variant: 'Metadata',
      fallbackExtension: extension,
    });
  }

  private async loadImageElement(imageUrl: string): Promise<HTMLImageElement> {
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Unable to load image for stamping'));
      image.src = imageUrl;
    });
  }

  private async getImagePageSize(imageUrl: string): Promise<{ width: number; height: number } | null> {
    try {
      const image = await this.loadImageElement(imageUrl);
      const { width, height } = this.getLoadedImagePageSize(image);

      if (!width || !height) {
        return null;
      }

      return { width, height };
    } catch (error) {
      console.warn('Unable to determine image dimensions for WebViewer page sizing:', error);
      return null;
    }
  }

  private getLoadedImagePageSize(image: HTMLImageElement): { width: number; height: number } {
    return {
      width: image.naturalWidth || image.width,
      height: image.naturalHeight || image.height,
    };
  }

  private async canvasToBlob(canvas: HTMLCanvasElement, mimeType: string): Promise<Blob> {
    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Canvas returned an empty blob'));
          return;
        }
        resolve(blob);
      }, mimeType, 0.92);
    });
  }

  private triggerBlobDownload(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  ngOnDestroy(): void {
    void this.persistAnnotationsNow();
    this.releaseActiveDocumentObjectUrl();

    if (this.annotationSaveTimeoutId) {
      clearTimeout(this.annotationSaveTimeoutId);
      this.annotationSaveTimeoutId = null;
    }

    if (this.webViewerInstance) {
      try {
        this.webViewerInstance.UI.dispose();
        this.webViewerInstance = null;
      } catch (error) {
        console.error('Error disposing WebViewer:', error);
      }
    }
  }

  private releaseActiveDocumentObjectUrl(): void {
    if (this.activeDocumentObjectUrl) {
      this.mediaFallbackService.releaseObjectUrl(this.activeDocumentObjectUrl);
      this.activeDocumentObjectUrl = null;
    }
  }
}
